import { useQuery } from "react-query";
import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import * as apiClient from "../api-client";
import { BsBuilding, BsMap, BsInfoCircle } from "react-icons/bs";
import { BiHotel, BiMoney, BiStar } from "react-icons/bi";

const MyHotels = () => {
  const { data: hotelData } = useQuery(
    "fetchMyHotels",
    apiClient.fetchMyHotels,
    {
      onError: () => { },
    }
  );

  const [searchQuery, setSearchQuery] = useState("");
  const [searchType, setSearchType] = useState("");
  const [searchCountry, setSearchCountry] = useState("");
  const [expandedRows, setExpandedRows] = useState<string[]>([]);
  const [showMetrics, setShowMetrics] = useState(false);

  const handleToggleExpand = (id: string) => {
    setExpandedRows((prevExpandedRows) =>
      prevExpandedRows.includes(id)
        ? prevExpandedRows.filter((rowId) => rowId !== id)
        : [...prevExpandedRows, id]
    );
  };

  const filteredHotels = useMemo(() => {
    return hotelData
      ? hotelData.filter(
        (hotel) =>
          hotel.name.toLowerCase().includes(searchQuery.toLowerCase()) &&
          hotel.type.some(type => type.toLowerCase().includes(searchType.toLowerCase())) &&
          hotel.country.toLowerCase().includes(searchCountry.toLowerCase())
      )
      : [];
  }, [hotelData, searchQuery, searchType, searchCountry]);

  const totalHotels = filteredHotels.length;

  const hotelsByCountry = useMemo(() => {
    const countByCountry: { [key: string]: number } = {};
    filteredHotels.forEach((hotel) => {
      countByCountry[hotel.country] = (countByCountry[hotel.country] || 0) + 1;
    });
    return countByCountry;
  }, [filteredHotels]);

  const hotelsByType = useMemo(() => {
    const countByType: { [key: string]: number } = {};
    filteredHotels.forEach((hotel) => {
      hotel.type.forEach((type) => {
        countByType[type] = (countByType[type] || 0) + 1;
      });
    });
    return countByType;
  }, [filteredHotels]);


  if (!hotelData) {
    return <span>No Hotels found</span>;
  }

  return (
    <div className="space-y-5 mt-20">
      <h1 className="text-3xl font-bold text-center">Search Hotels</h1>
      <hr className="border-gray-200 w-10/12 mx-auto" />
      <div className="space-y-3">
        <div className="flex justify-between space-x-2">
          <input
            type="text"
            placeholder="Search by hotel name"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-halo-pink md:w-3/12"
          />
          <input
            type="text"
            placeholder="Search by type"
            value={searchType}
            onChange={(e) => setSearchType(e.target.value)}
            className="border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-halo-pink md:w-3/12"
          />
          <input
            type="text"
            placeholder="Search by country"
            value={searchCountry}
            onChange={(e) => setSearchCountry(e.target.value)}
            className="border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-halo-pink md:w-3/12"
          />
          <Link
            to="/add-hotel"
            className="md:w-2/12 h-[45px] md:ml-4 mx-auto bg-halo-pink px-6 py-2.5 text-sm font-bold uppercase tracking-wide text-white transition-none hover:bg-pink-800 sm:mt-0 sm:w-auto sm:shrink-0 text-center rounded-md"
          >
            Add Hotel
          </Link>
        </div>
        <div className="text-center">
          <button
            onClick={() => setShowMetrics(!showMetrics)}
            className="text-blue-600 hover:underline mt-2 uppercase"
          >
            {showMetrics ? "Hide Metrics" : "Show Metrics"}
          </button>
        </div>
        {showMetrics && (
          <section>
            <div className="flex py-5 justify-between space-x-2">
              <div className="flex items-center justify-center bg-gray-50 text-xl font-semibold shadow-lg rounded-lg p-6 w-1/3">
                <div className="text-center">
                  <div className="text-gray-700 uppercase mb-2">Total Hotels</div>
                  <div className="text-4xl font-bold text-halo-pink">
                    {totalHotels}
                  </div>
                </div>
              </div>


              <div className="bg-white text-lg text-halo-pink font-semibold p-3 rounded-md shadow-md w-1/3">
                <div className="border-b pb-2 mb-2 text-center">Countries</div>
                {Object.entries(hotelsByCountry).map(([country, count]) => (
                  <div key={country} className="flex justify-between items-center border-b pb-1 mb-1">
                    <span className="text-black">{country}</span>
                    <span className="ml-2 font-normal">{count}</span>
                  </div>
                ))}
              </div>

              <div className="bg-white text-lg text-halo-pink font-semibold p-3 rounded-md shadow-md w-1/3">
                <div className="border-b pb-2 mb-2 text-center">Holiday Types</div>
                {Object.entries(hotelsByType).map(([type, count]) => (
                  <div key={type} className="flex justify-between items-center border-b pb-1 mb-1">
                    <span className="text-black">{type}</span>
                    <span className="ml-2 font-normal">{count}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full bg-white">
          <thead>
            <tr className="bg-gray-100">
              <th className="py-3 px-4 border-b text-center">
                <div className="flex flex-col items-center">
                  <BsBuilding className="inline mb-1" />
                  Hotel Name
                </div>
              </th>
              <th className="py-3 px-4 border-b text-center">
                <div className="flex flex-col items-center">
                  <BsInfoCircle className="inline mb-1" />
                  Description
                </div>
              </th>
              <th className="py-3 px-4 border-b text-center">
                <div className="flex flex-col items-center">
                  <BsMap className="inline mb-1" />
                  Location
                </div>
              </th>
              <th className="py-3 px-4 border-b text-center">
                <div className="flex flex-col items-center">
                  <BsBuilding className="inline mb-1" />
                  Type
                </div>
              </th>
              <th className="py-3 px-4 border-b text-center">
                <div className="flex flex-col items-center">
                  <BiMoney className="inline mb-1" />
                  Price/Night
                </div>
              </th>
              <th className="py-3 px-4 border-b text-center">
                <div className="flex flex-col items-center">
                  <BiHotel className="inline mb-1" />
                  Occupancy
                </div>
              </th>
              <th className="py-3 px-4 border-b text-center">
                <div className="flex flex-col items-center">
                  <BiStar className="inline mb-1" />
                  Rating
                </div>
              </th>
              <th className="py-3 px-4 border-b text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredHotels.map((hotel, index) => (
              <tr key={hotel._id} className={index % 2 === 0 ? "bg-gray-50" : "bg-white"}>
                <td className="py-2 px-4 border-b">{hotel.name}</td>
                <td className="py-2 px-2 border-b whitespace-pre-line">
                  {expandedRows.includes(hotel._id)
                    ? hotel.description
                    : `${hotel.description.slice(0, 100)}...`}
                  <button
                    onClick={() => handleToggleExpand(hotel._id)}
                    className="text-blue-600 hover:underline ml-2"
                  >
                    {expandedRows.includes(hotel._id) ? "See less" : "See more"}
                  </button>
                </td>
                <td className="py-2 px-4 border-b text-center">{hotel.country}</td>
                <td className="py-2 px-4 border-b text-center">{hotel.type}</td>
                <td className="py-2 px-4 border-b text-center">${hotel.pricePerNight}</td>
                <td className="py-2 px-3 border-b text-center">
                  {hotel.adultCount} AD, {hotel.childCount} CH
                </td>
                <td className="py-2 border-b text-center">{hotel.starRating} Star</td>
                <td className="py-2 px-4 border-b">
                  <Link
                    to={`/resort/${hotel.slug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-4 py-2.5 border text-green-600 text-sm rounded uppercase border-green-600 hover:bg-green-600 hover:text-white transition-colors duration-200 mr-2"
                  >
                    View
                  </Link>
                  <Link
                    to={`/edit-hotel/${hotel.slug}`}
                    className="px-4 py-2.5 border text-green-600 text-sm rounded uppercase border-green-600 hover:bg-green-600 hover:text-white transition-colors duration-200"
                  >
                    Edit
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default MyHotels;